import { useEffect, useRef, useState } from 'react'

import type { Density, Grid, ViewKey } from './views'
import { openViews } from './views'

import { useBarsHidden } from '~/components/useBars'
import { AppLink } from '~/components/AppLink'

import s from './Toolbar.module.css'

/**
 * The calendar's own controls, ported from `_calendar_page.py`'s `.top` row and the tab row
 * under it.
 *
 * The masthead is above this and is not part of it. In the Python the two are one `<header>`
 * and one stylesheet, which is how the calendar ended up with a second `.nav` and a second
 * `.top`; here the masthead is `Shell`'s and this component draws only what belongs to the
 * calendar.
 *
 * Everything is a button and nothing is a link. A tab, a grid or a density changes what this
 * page is showing rather than where the reader is, and the address is written back by the
 * owner of that state — see `address.ts`.
 */

/**
 * Which way a sideways row still has items past its edge, published on the row as
 * `data-scrolls`.
 *
 * It is an attribute rather than a class because it is the row's own answer about itself and
 * the stylesheet is only one reader of it: a test can ask the row which edge it is cut at
 * without measuring a pixel, which is the only way the mark at that edge is checkable at all.
 */
type Scrolls = 'none' | 'right' | 'left' | 'both'

/** Local on purpose: only the tab row cares, and only about whether there is anybody. */
export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

/** The segmented switch, in the Python's order. */
const MODES: readonly (readonly [Grid, string])[] = [
  ['month', 'Month'],
  ['week', 'Week'],
  ['list', 'List'],
]

const DENSITIES: readonly (readonly [Density, string])[] = [
  ['full', 'Full'],
  ['compact', 'Compact'],
]

export interface ToolbarProps {
  view: ViewKey
  grid: Grid
  density: Density
  /**
   * How many gigs each tab holds, keyed by its own key.
   *
   * The Python printed these and the port dropped them. They are worth having because the
   * tabs are five different questions and the number is the only part of the answer visible
   * before you press: a Liked with nothing in it and a Liked with forty look identical
   * otherwise, and one of them is worth a click.
   */
  counts?: Partial<Record<ViewKey, number>>
  /** Null for a signed-out reader, who has no lists of their own and is shown two tabs. */
  me: Me | null
  /** Already formatted — "August 2026", or the week's range. The month arrows are disabled
   *  either side of what the payload actually covers, which only the caller knows. */
  monthLabel: string
  canPrev: boolean
  canNext: boolean
  onView(v: ViewKey): void
  onGrid(g: Grid): void
  onDensity(d: Density): void
  /**
   * -1 back, +1 forward. One step, not one month: the Python's own arrow handler branches
   * on the mode and calls `shiftWeek(±7)` in the week view, because paging a week view by a
   * month skips three weeks of gigs. The caller applies the unit its grid pages by.
   */
  onStep(delta: number): void
  /**
   * Back to tonight, from wherever the reader has paged to.
   *
   * The arrows move one step and there was no way back but pressing the other one the same
   * number of times, or reloading. Worse on arrival than on the way back: the list opens on
   * the anchored month and a reader landing in September met August's finished nights first,
   * with today somewhere below the fold and nothing on screen saying so.
   */
  onToday(): void
  /**
   * The next few nights that actually have something on, newest first, as `YYYY-MM-DD`.
   *
   * A calendar's commonest question is a date — "what's on Friday?" — and the only way to ask
   * it here was to page a month at a time and scroll. The chips are that question, answered
   * in one press, and they are drawn from the loaded rows rather than from a calendar widget
   * so a night with nothing on it is never offered.
   */
  days?: readonly string[]
  /** The anchored night, so the chip for it can say so. */
  on?: string
  onDay?(night: string): void
  /** Whether the anchored night is already today's, which is when the control has nothing to
   *  offer and is not drawn. */
  atToday?: boolean
}

/** The weekday over the date: two short lines rather than one long one, which is what lets a
 *  row of these stay a row on a 390px screen. */
function chipParts(night: string): readonly [string, string] {
  const d = new Date(`${night}T12:00:00Z`)
  const at = (opt: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString('en-GB', { timeZone: 'UTC', ...opt }).toUpperCase()
  return [at({ weekday: 'short' }), at({ day: 'numeric' })]
}

/**
 * A row that scrolls sideways on a phone: the item chosen in it brought into view, and which
 * way it is cut published as `data-scrolls` for the fade at that edge.
 *
 * The tab row and the day chips are both such rows, and both run on this, so the guard, the
 * options and the trigger are written once. Two copies would agree on the day they were made
 * and part at the next fix to either, and then the two rows under one thumb behave
 * differently. The stylesheet's half — the fade, its length and the scroll padding that
 * keeps the chosen item out of it — is shared the same way, in one set of rules selecting
 * both rows.
 *
 * `chosen` finds the chosen item inside the row: `aria-selected` for a tab, `aria-pressed`
 * for a chip. `at` is what chooses it, and is the address's value rather than anything held
 * here, so the address stays the one writer: a press writes the address, the address comes
 * back as `at`, and the Back button and a pasted link move the row the way a tap does.
 *
 * `shape` is the row's contents as one string. The effects want to run when the items in
 * the row, or their widths, change, and the props those come from are objects the caller
 * may rebuild on every render with nothing in them changed — a fresh `counts`, or a fresh
 * `days` array each time the entries reload. Keyed on those, a row the reader has dragged
 * would be dragged back under their thumb whenever the page re-rendered. A string compares
 * by what it says.
 *
 * Each row gets its own call, so its own effects and its own state: one row's selection
 * changing moves that row and not the other, and each reads its own metrics.
 */
function useSideways(chosen: string, at: string | undefined, shape: string) {
  const row = useRef<HTMLDivElement>(null)
  const [scrolls, setScrolls] = useState<Scrolls>('none')

  /*
   * The chosen item, brought into the row.
   *
   * The rows have always been scrollers on a phone and nothing ever moved them, so the item
   * you are on could be cut in half or wholly off the screen on arrival — a shared
   * `?view=all` link opened on a tab row scrolled to the left with the tab it names past the
   * right edge, and `?on=` a night late in the chips the same with its chip.
   *
   * Keyed on `at` and on the row's `shape` and on nothing else. `at` because it is what
   * chooses the item; the shape because the row's contents arrive after the first paint and
   * move every item when they do — a tab's count, or the nights once the entries load — so
   * an item that was whole before the payload landed is not afterwards. The shape also
   * changes from empty when a row that was not drawn is drawn, which is how opening the
   * list brings its chip into view. A press is deliberately not a trigger: see `at` above.
   *
   * `block: 'nearest'` and not `block: 'start'`. `'start'` scrolls the *page* to put the row
   * at the top of the window — which is what `ListView` and `Calendar` want when they open a
   * month, and is the wrong answer for a control the reader is already looking at.
   *
   * `'nearest'` alone does not keep the page still, though. `scrollIntoView` scrolls every
   * scrolling ancestor, the window included, and `'nearest'` leaves the window alone only
   * while the item is already wholly inside it. Above the phone breakpoint the bar is
   * translated off the top of the window while the reader scrolls down (`useBarsHidden`), so
   * a count changing then — a save landing with the next month's entries, an ingest between
   * fetches — or Back changing `view` would scroll the window up to the tab and pull the bars
   * back in. Hence the guard: an item that is not wholly on the screen vertically is one the
   * reader is not looking at, and nothing is scrolled for it. It reads the item's box rather
   * than `away` because the bar is also part-way off the screen for the length of its slide
   * back in, after `away` has already turned false.
   *
   * `inline: 'nearest'` is the least movement that makes the item whole, so the items either
   * side stay in sight; how far clear of the cut edge it lands is `scroll-padding-inline`'s
   * to say, because `'nearest'` has no offset of its own.
   *
   * No item chosen, no movement: a night picked from the date control that has no chip of its
   * own presses nothing, and there is nothing to bring into view.
   *
   * No `behavior`, so the row takes its own `scroll-behavior`, and nothing gives a row one:
   * the property is not inherited, so `base.css`'s smooth `html` reaches the window and not
   * the row. Instant is wanted, not tolerated. A chip press also asks `Calendar` for a smooth
   * scroll of the page down to the night, and the row's movement is not the reader's
   * business to watch alongside it.
   *
   * Called defensively: jsdom gives its elements no `scrollIntoView`, and the row is opened
   * to the right item with or without the method.
   */
  useEffect(() => {
    const selected = row.current?.querySelector(chosen)
    if (!selected) return
    const { top, bottom } = selected.getBoundingClientRect()
    if (top < 0 || bottom > window.innerHeight) return
    selected.scrollIntoView?.({ inline: 'nearest', block: 'nearest' })
  }, [chosen, at, shape])

  /*
   * Which way the row still has items, published for the mark at the cut edge.
   *
   * A scrollbar is the browser's own answer to this and the phone rules switch it off, for a
   * reason that still holds — a scrollbar drawn across a row this short is most of the row —
   * so the row has to say it some other way.
   *
   * Recomputed on the row's own scroll, on a resize of the row, and whenever the chosen item
   * or the row's shape changes, because the effect above may have moved the row and new
   * contents change how wide it is. The shape is also what lets go of a row that is no longer
   * drawn: it changes when the row goes, so the cleanup below runs for the element that left.
   *
   * The one-pixel slack is margin, not a fix for an observed case: an exact comparison
   * against 0 or against `scrollWidth - clientWidth` would read a row parked at its end as
   * one with more to give if either side ever came back fractional, and a pixel of an item
   * behind the fade is nothing a reader can see.
   */
  useEffect(() => {
    const el = row.current
    if (!el) return
    const read = () => {
      const slack = el.scrollWidth - el.clientWidth
      if (slack <= 1) return setScrolls('none')
      const back = el.scrollLeft > 1
      const on = el.scrollLeft < slack - 1
      setScrolls(back && on ? 'both' : back ? 'left' : 'right')
    }
    read()
    el.addEventListener('scroll', read, { passive: true })
    const watch = new ResizeObserver(read)
    watch.observe(el)
    return () => {
      el.removeEventListener('scroll', read)
      watch.disconnect()
    }
  }, [chosen, at, shape])

  return { row, scrolls }
}

export function Toolbar({
  view,
  grid,
  density,
  me,
  monthLabel,
  canPrev,
  canNext,
  counts,
  onView,
  onGrid,
  onDensity,
  onStep,
  onToday,
  atToday = false,
  days = [],
  on,
  onDay,
}: ToolbarProps) {
  // With the counts: My venues is drawn only when there is something under it.
  const tabs = openViews(me, counts)
  // The same reading of the same scroll the masthead uses, so the two are never out of step.
  const away = useBarsHidden()
  const bar = useRef<HTMLDivElement>(null)

  /*
   * The tab row's contents as one string: which tabs there are and what number each carries.
   *
   * The row's effects in `useSideways` want to run when the row's width changes, and
   * `counts` is the wrong thing to watch for that. It is an object this component is handed
   * rather than one it owns, and a caller that builds a fresh one per render — the calendar
   * page does, for a reader — would fire them on every render, which on a row the reader has
   * dragged means dragging it back under their thumb. What actually moves a tab's edge is a
   * digit changing or a tab arriving, and both of those change this string.
   */
  const shape = tabs.map(tab => `${tab.key}:${counts?.[tab.key] ?? ''}`).join(' ')
  const tabRow = useSideways('[aria-selected="true"]', view, shape)

  /*
   * The chip row's contents, the same way: the nights, and whether the date control is at
   * the end of them. `days` is rebuilt by the page whenever its entries reload — a mark
   * pressed, the list widening as the reader scrolls — with the same nights in it, and only
   * a change in what the row holds may move it. Empty exactly when the row is not drawn, so
   * drawing it and taking it away are both changes of shape.
   */
  const nights = days.length ? `${days.join(' ')}${onDay ? ' pick' : ''}` : ''
  const chipRow = useSideways('[aria-pressed="true"]', on, nights)

  /*
   * Where the sticky block ends, published as `--headh` for whatever sticks under it — the
   * week's day headers and the list's day bars.
   *
   * Both of them read `--headh` and nothing had ever set it, so it fell back to `0px` and
   * they stuck to the very top of the window, sliding under the masthead and this bar on
   * the way up. The comment in `WeekView.module.css` said it was "published on the root by
   * the calendar"; it was not.
   *
   * The masthead's height *plus* this bar's, because a day header sits below both. Zero
   * while the bars are away, on the same reasoning `--mast-h` uses: the thing below rises
   * into the space rather than leaving a strip of nothing where the bar used to be.
   */
  useEffect(() => {
    const el = bar.current
    if (!el) return
    const publish = () => {
      const mast = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--mast-h') || '0',
      )
      const height = away ? 0 : (mast || 0) + el.getBoundingClientRect().height
      document.documentElement.style.setProperty('--headh', `${Math.round(height)}px`)
    }
    publish()
    const watch = new ResizeObserver(publish)
    watch.observe(el)
    return () => watch.disconnect()
  }, [away])

  return (
    <>
      {/*
        * The arrows, the month, the switches and the tabs are one block, and it stays on
        * screen. Scrolling December pushed the month name and the five tabs off the top, so
        * "which view am I in" and "which month is this" were both a scroll back up.
        *
        * It goes with the masthead rather than instead of it: away on the way down, back the
        * moment you turn round. Permanently stuck was the first version and it was wrong —
        * a bar that never leaves is a bar you are always reading past, and the calendar
        * already gives up its top two inches to chrome before the first gig.
        *
        * It rides under the masthead at `top: var(--mast-h)`, which `Shell` measures and
        * drops to zero while the masthead is away — so the translate takes it fully off the
        * screen rather than leaving it sitting where the masthead used to be.
        *
        * On a phone neither bar goes: `useBarsHidden` answers `false` there, for reasons it
        * states, and this bar takes that answer rather than holding a view of its own.
        *
        * The sentence under the tabs is deliberately outside: it is a paragraph, it is read
        * once, and it would be a third of the sticky block's height for the life of the page.
        */}
      <div ref={bar} className={away ? `${s.bar} ${s.away}` : s.bar}>
      <div className={s.top}>
        {/*
         * The labels say "month" in every grid, which is what the Python says and is wrong
         * in the week view, where the arrow moves a week. Kept as it is; see the report.
         *
         * Chips or arrows, never neither. The day chips below page the phone's list by
         * night and are the better control there; the arrows are the only paging control
         * the month and the week have on a phone, and the only one an empty tab has. The
         * bar was hiding the arrows by width, which was right the day the chips were drawn
         * on every phone grid and wrong the day after, when the chips narrowed to the list
         * and the month and the week were left with no way forwards or back. So the rule
         * is the chips' own presence, decided here, where `days` is: whichever of the two
         * is drawn, the reader can move.
         */}
        {days.length ? null : (
          <div className={s.nav}>
            <button aria-label="Previous month" disabled={!canPrev} onClick={() => onStep(-1)}>
              ‹
            </button>
            <button aria-label="Next month" disabled={!canNext} onClick={() => onStep(1)}>
              ›
            </button>
          </div>
        )}

        {/*
          * The page's heading, and until now the calendar had none at all —
          * `querySelectorAll('h1,…,h6')` on `/calendar` returned an empty list where the
          * front page returns nine, and nothing here was larger than 12px. It reads as a
          * different site for that reason more than any other.
          *
          * It stays inside the sticky bar rather than sitting above it. The month is the
          * one fact you need at every scroll depth — which is why it was put here — and a
          * heading above the bar would either scroll away or be printed twice on screen.
          * It costs one pixel: the nav buttons beside it are 28px and already set the row.
          */}
        <h1 className={s.monthname}>{monthLabel}</h1>

        {/*
          * The way back, and the only accent on this bar.
          *
          * `tokens.css` reserves the accent for money and for anything you can act on: this is
          * the one control here that returns the reader somewhere rather than changing how the
          * same thing is drawn, and it is the one thing on the bar they will want when lost.
          *
          * Absent when it would do nothing — a button that is already where it would take you
          * is a control that answers a press by not moving, which reads as broken.
          */}
        <button
          type="button"
          className={s.today}
          /*
           * Always drawn, and disabled where it would do nothing.
           *
           * Hiding it was the first arrangement and it was wrong twice over: a control that
           * appears only once the reader is lost is one they have never seen before the
           * moment they need it, and a button entering and leaving the bar shuffles every
           * switch beside it sideways whenever the month changes.
           */
          disabled={atToday}
          onClick={onToday}
        >
          Today
        </button>

        <span className={s.spacer} />

        {/* The finder sits here in the Python, between the spacer and the switches. */}

        <div className={s.seg}>
          {MODES.map(([key, label]) => (
            <button key={key} aria-pressed={grid === key} onClick={() => onGrid(key)}>
              {label}
            </button>
          ))}
        </div>

        {/*
         * Offered in the list and nowhere else: a month cell shows a chip whichever density
         * is set, so the switch did nothing there but sit next to the mode switch looking
         * like part of it. Hidden rather than unmounted, as the Python has it.
         */}
        <div className={s.seg} hidden={grid !== 'list'}>
          {DENSITIES.map(([key, label]) => (
            <button key={key} aria-pressed={density === key} onClick={() => onDensity(key)}>
              {label}
            </button>
          ))}
        </div>

        {/*
         * The way out to a calendar app, and it is here because this is the page the gigs are
         * on. Signed in only — there is nothing to export without a list to export.
         *
         * **Shown to somebody with nothing saved, deliberately.** The spec's D3 settles the
         * empty case as a sentence on the export page, and a control hidden until you have
         * saved a gig never delivers that sentence: it leaves a reader who has heard the feed
         * exists with nowhere to go and nothing to read. The gate is on minting, at the
         * endpoint, and not on this button.
         */}
        {me ? (
          <AppLink to="/export" className={s.export}>
            Export
          </AppLink>
        ) : null}

        {/*
         * There is no Briefing button, and there were two — this and the phone bar's. It is
         * on the masthead now, where it is reachable from every page rather than only from
         * the calendar. Which was the actual complaint behind "I can't find the button".
         */}
      </div>

      {/*
       * Three of the five views are about you — what you hearted, where you said you would
       * be, what your friends are going to — and a stranger has none of those. Showing them
       * greyed out would advertise five doors and open two; showing two is the honest shape
       * of what a signed-out calendar is.
       */}
      {/*
        * The next few nights that have something on, as chips.
        *
        * Under the month and above the tabs, because these narrow *when* and the tabs narrow
        * *whose*, and that is the order a reader asks the two questions in. Drawn from the
        * rows already loaded, so a night with nothing on it is never offered — which is the
        * difference between this and a date picker, and the reason it is worth the width.
        */}
      {days.length ? (
        <div ref={chipRow.row} className={s.days} data-scrolls={chipRow.scrolls}>
          {days.map(night => (
            <button
              key={night}
              type="button"
              className={s.day}
              aria-pressed={night === on}
              onClick={() => onDay?.(night)}
            >
              {chipParts(night).map(part => (
                <span key={part}>{part}</span>
              ))}
            </button>
          ))}
          {/*
            * Any other night, for the question the chips cannot answer.
            *
            * A native `date` input rather than a calendar of our own: the reader's phone
            * already has one they know, it is reachable by keyboard and by screen reader
            * without any of that being built here, and it is two elements instead of a
            * component. The label sits on top of it because a bare date input draws its own
            * placeholder, which is the one part of the native control that would not match
            * anything else on this bar.
            */}
          {onDay ? (
            <label className={`${s.day} ${s.pick}`}>
              <span>PICK A</span>
              <span>DAY</span>
              <input
                type="date"
                aria-label="Go to a date"
                value={on ?? ''}
                onChange={ev => ev.target.value && onDay(ev.target.value)}
              />
            </label>
          ) : null}
        </div>
      ) : null}

      <div ref={tabRow.row} className={s.tabs} data-scrolls={tabRow.scrolls}>
        {/*
          * The next few nights, as chips.
          *
          * Under the month and above the tabs, because it narrows *when* while the tabs
          * narrow *whose* — two different questions, and stacking them in that order is the
          * order a reader asks them in.
          */}
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={s.tab}
            aria-selected={view === tab.key}
            onClick={() => onView(tab.key)}
          >
            {tab.label}
            {counts?.[tab.key] === undefined ? null : (
              <span className={s.n}>{counts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>
      </div>

      {/* What the list under it claims, and who can see it. */}
      {/* The per-view sentence is gone. Five tabs that each explained themselves in a
          paragraph, on a page whose job is to be scanned — and the sentence changed under
          the reader every time they moved between tabs. The tab names carry it. */}
    </>
  )
}
